import * as XLSX from 'xlsx';
import { isPlaceholderStudentName, normalizeStudentNameInput } from './studentNamePolicy';
import { parsePastedNames } from './classRosterManage';
import { readClassRosterFileRows } from '../api/classRosterApi';

const SERVER_EXTENSIONS = new Set(['hwp', 'hwpx', 'docx', 'pdf']);
export const ROSTER_FILE_EXTENSIONS = ['xlsx', 'xls', 'csv', 'txt', 'hwp', 'hwpx', 'docx', 'pdf'];
export const ROSTER_FILE_ACCEPT = ROSTER_FILE_EXTENSIONS.map(ext => `.${ext}`).join(',');

const NAME_HEADERS = new Set(['이름', '성명', '학생명', '학생이름', '학생성명', 'name']);
const HEADER_SCAN_ROWS = 15;
const KOREAN_NAME = /^[가-힣]{2,5}$/;

const headerKey = (cell) => normalizeStudentNameInput(cell).replace(/\s+/g, '').toLowerCase();

const isNameCell = (cell) => {
  const text = normalizeStudentNameInput(cell);
  return text.length >= 2
    && text.length <= 20
    && !/[\d:]/.test(text)
    && !NAME_HEADERS.has(headerKey(text))
    && !isPlaceholderStudentName(text);
};

/** 나이스 명렬표처럼 제목 줄·성별·생년월일 열이 섞인 표에서 이름 열만 읽는다. 좌우 두 단 명렬표도 왼쪽 단부터 읽는다. */
export const extractNamesFromRows = (rows) => {
  const table = (rows || []).map(row => (row || []).map(cell => String(cell ?? '')));
  const headerIndex = table.slice(0, HEADER_SCAN_ROWS).findIndex(row => row.some(cell => NAME_HEADERS.has(headerKey(cell))));
  if (headerIndex >= 0) {
    const columns = table[headerIndex].map((cell, index) => (NAME_HEADERS.has(headerKey(cell)) ? index : -1)).filter(index => index >= 0);
    return columns.flatMap(column => table.slice(headerIndex + 1)
      .map(row => row[column])
      .filter(isNameCell)
      .map(normalizeStudentNameInput));
  }
  const width = Math.max(0, ...table.map(row => row.length));
  let best = { column: -1, count: 0 };
  for (let column = 0; column < width; column += 1) {
    const count = table.filter(row => KOREAN_NAME.test(normalizeStudentNameInput(row[column]))).length;
    if (count > best.count) best = { column, count };
  }
  if (best.count < 2) return [];
  return table
    .map(row => normalizeStudentNameInput(row[best.column]))
    .filter(cell => KOREAN_NAME.test(cell) && isNameCell(cell));
};

const readAsArrayBuffer = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsArrayBuffer(file);
});

/** 학교에서 받은 CSV는 대부분 EUC-KR 이다. UTF-8로 깨지면 EUC-KR 로 다시 읽는다. */
export const decodeTextFile = (buffer) => {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer).replace(/^\uFEFF/, '');
  } catch (error) {
    return new TextDecoder('euc-kr').decode(buffer);
  }
};

const textToRows = (text) => String(text || '').split(/\r?\n/).map(line => line.split(/\t|,/));

const extension = (name) => (String(name || '').match(/\.([^.]+)$/)?.[1] || '').toLowerCase();

const namesFromRows = (rows) => {
  const names = extractNamesFromRows(rows);
  return names.length >= 2 ? names : parsePastedNames(rows.map(row => row.join('\t')).join('\n'));
};

/** 학급 관리 '파일에서 불러오기': 엑셀·CSV·텍스트는 브라우저에서, 한글·워드·PDF는 서버가 표로만 바꿔 준 뒤 이름 목록을 돌려준다. */
export const readRosterFile = async (file) => {
  const ext = extension(file?.name);
  if (SERVER_EXTENSIONS.has(ext)) return namesFromRows(await readClassRosterFileRows(file));
  const buffer = await readAsArrayBuffer(file);
  if (ext === 'csv' || ext === 'txt') {
    const text = decodeTextFile(buffer);
    const names = extractNamesFromRows(textToRows(text));
    return names.length >= 2 ? names : parsePastedNames(text);
  }
  const book = XLSX.read(buffer, { type: 'array' });
  for (const sheetName of book.SheetNames) {
    const rows = XLSX.utils.sheet_to_json(book.Sheets[sheetName], { header: 1, raw: false, defval: '' });
    const names = extractNamesFromRows(rows);
    if (names.length >= 2) return names;
  }
  return [];
};
